import { Component, effect, ElementRef, inject, signal, viewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelDescription, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { type ModelActionPoint, type NormalizedDeviceData, type RemoteAction } from '@devices/models';
import { DeviceRemoteActionService, DeviceService } from '@devices/services';
import { ConfirmationDialogComponent, NotificationsService } from '@ui';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { finalize } from 'rxjs';

import { ActionPointPickerComponent } from '../action-point-picker/action-point-picker.component';
import { DevicePickerComponent } from '../device-picker';

enum ExpansionPanels {
  DeviceSelection,
  ActionPointSelection,
  RemoteActionName,
}

@Component({
  imports: [
    MatDialogModule,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription,
    DevicePickerComponent,
    MatAccordion,
    ActionPointPickerComponent,
    MatFormField,
    ReactiveFormsModule,
    MatInput,
    FormsModule,
    NgxSkeletonLoaderModule,
  ],
  selector: 'avs-remote-action-settings-dialog',
  standalone: true,
  styleUrl: './remote-action-settings-dialog.component.scss',
  templateUrl: './remote-action-settings-dialog.component.html',
})
export class RemoteActionSettingsDialogComponent {
  protected form: FormGroup;
  protected openedIndex = signal<number | null>(ExpansionPanels.DeviceSelection);
  protected selectedDeviceName = signal<string | null>(null);
  protected selectedDeviceId = signal<string | null>(null);
  protected selectedActionPointName = signal('');
  protected isEditMode = signal(false);
  protected isLoading = signal(false);
  protected data = inject<{ deviceId: string, remoteAction: RemoteAction | null; }>(MAT_DIALOG_DATA);
  private remoteActionService = inject(DeviceRemoteActionService);
  private dialogRef = inject(MatDialogRef<RemoteActionSettingsDialogComponent>);
  private notificationService = inject(NotificationsService);
  private deviceService = inject(DeviceService);
  private dialog = inject(MatDialog);
  private fb = inject(FormBuilder);
  private actionNameInput = viewChild<ElementRef<HTMLInputElement>>('actionName');


  constructor() {
    this.form = this.fb.group({
      deviceId: [this.data.deviceId, Validators.required],
      name: ['', Validators.required],
      remoteDeviceActionPointId: ['', Validators.required],
    });
    effect(() => {
      if (this.data.remoteAction) {
        this.form.patchValue({
          deviceId: this.data.remoteAction.deviceId,
          name: this.data.remoteAction.name,
          remoteDeviceActionPointId: this.data.remoteAction.remoteDeviceActionPointId,
        });
        this.isLoading.set(true);
        this.deviceService.getDeviceDetailsByDeviceId(this.data.remoteAction.deviceId)
          .pipe(finalize(() => this.isLoading.set(false)))
          .subscribe((device) => {
            this.selectedDeviceName.set(device.friendlyName);
            this.selectedDeviceId.set(device.id);
          });
        this.isEditMode.set(true);
        this.openedIndex.set(ExpansionPanels.RemoteActionName);
      }
    });
  }

  protected addRemoteAction() {
    this.remoteActionService.addNewRemoteAction(this.form.value).subscribe({
      error: (error) => {
        this.notificationService.showErrorMessage(error.message);
      },
      next: () => {
        this.dialogRef.close({ fetch: true });
        this.notificationService.showSuccessMessage('Remote action was successfully created');
      },
    });
  }

  protected updateRemoteAction() {
    this.remoteActionService.updateRemoteAction(this.data.remoteAction!.id, {
      name: this.form.get('name')!.value,
      remoteDeviceActionPointId: this.form.get('remoteDeviceActionPointId')!.value,
    }).subscribe({
      error: (error) => {
        this.notificationService.showErrorMessage(error.message);
      },
      next: () => {
        this.dialogRef.close({ fetch: true });
        this.notificationService.showSuccessMessage('Remote action was successfully updated');
      },
    });
  }

  protected deviceSelected($event: NormalizedDeviceData[]) {
    if ($event.length > 0) {
      const device = $event[0];

      this.form.patchValue({
        name: '',
        remoteDeviceActionPointId: '',
      });

      this.selectedDeviceName.set(device.deviceName);
      this.selectedDeviceId.set(device.id);
      this.openedIndex.set(ExpansionPanels.ActionPointSelection);
    }
  }

  protected selectActionPoint($event: ModelActionPoint) {
    this.selectedActionPointName.set($event.name);
    this.form.patchValue({
      name: this.form.get('name')!.value || $event.name,
      remoteDeviceActionPointId: $event.deviceActionPointsId,
    });
    if (!this.isEditMode()) {
      this.openedIndex.set(ExpansionPanels.RemoteActionName);
      setTimeout(() => this.actionNameInput()?.nativeElement.focus(), 300);
    }
  }

  protected removeRemoteAction() {
    this.openConfirmationDialog({
      description: 'Are you sure you want to delete this remote action?',
      title: 'Delete remote action',
    });
  }

  private openConfirmationDialog(
    {
      title,
      description,
    }: {
      description: string;
      title: string;
    }) {
    ConfirmationDialogComponent.open(this.dialog, {
      cancelBtnText: 'Cancel',
      confirmBtnText: 'Delete',
      description,
      isDeleteTypePopup: true,
      showWarning: true,
      title,
    }).subscribe(confirmation => {
      if (confirmation) {
        this.remoteActionService.deleteRemoteAction(this.data.remoteAction!.id).subscribe({
          error: (error) => {
            this.notificationService.showErrorMessage(error.message);
          },
          next: () => {
            this.dialogRef.close({ fetch: true });
          },
        });
      }
    });
  }
}
